import React from 'react';

function RoomGrid({ rooms, onRoomClick, showLegend = false }) {
  // Organiser les chambres par étage
  const roomsByFloor = rooms.reduce((acc, room) => {
    if (!acc[room.floor]) {
      acc[room.floor] = [];
    }
    acc[room.floor].push(room);
    return acc;
  }, {});

  // Trier les étages par ordre décroissant (5, 4, 3, 2, 1)
  const floors = Object.keys(roomsByFloor).sort((a, b) => b - a);

  const getStatusDisplay = (room) => {
    switch (room.status) {
      case 'available':
        return {
          status: 'available',
          icon: '🟢',
          label: 'Libre',
          color: 'var(--available-green)',
          bg: 'var(--available-green)'
        };
      case 'occupied':
        return {
          status: 'occupied',
          icon: '🔴',
          label: 'Occupée',
          color: 'var(--occupied-coral)',
          bg: 'var(--occupied-coral)'
        };
      case 'reserved':
        return {
          status: 'reserved',
          icon: '🔵',
          label: 'Réservée',
          color: 'var(--reserved-blue)',
          bg: 'var(--reserved-blue)'
        };
      case 'maintenance':
        return {
          status: 'maintenance',
          icon: '🔘',
          label: 'Maintenance',
          color: 'var(--maintenance-amber)',
          bg: 'var(--maintenance-amber)'
        };
      case 'cleaning':
        return {
          status: 'cleaning',
          icon: '🟡',
          label: 'Nettoyage',
          color: 'var(--cleaning-amber)',
          bg: 'var(--cleaning-amber)'
        };
      default:
        return {
          status: 'available',
          icon: '⚪',
          label: 'Indéfini',
          color: 'var(--text-muted)',
          bg: 'var(--background)'
        };
    }
  };

  const getRoomTypeIcon = (type) => {
    switch (type) {
      case 'single': return '🛏️';
      case 'double': return '🛏️🛏️';
      case 'suite': return '👑';
      default: return '🏠';
    }
  };

  const getFloorStats = (floor) => {
    const floorRooms = roomsByFloor[floor];
    return {
      total: floorRooms.length,
      available: floorRooms.filter(r => r.status === 'available').length,
      occupied: floorRooms.filter(r => r.status === 'occupied').length,
      reserved: floorRooms.filter(r => r.status === 'reserved').length,
      maintenance: floorRooms.filter(r => r.status === 'maintenance').length,
      cleaning: floorRooms.filter(r => r.status === 'cleaning').length
    };
  };

  return (
    <div className="room-grid-professional">
      <div className="grid-header">
        <div className="grid-title-section">
          <h2 className="grid-title">
            <span className="title-icon">🏨</span>
            Plan des Chambres
          </h2>
          <div className="grid-subtitle">
            Vue d'ensemble par étage - {rooms.length} chambres au total
          </div>
        </div>

        {showLegend && (
          <div className="grid-legend-compact">
            <div className="legend-title">Statuts</div>
            <div className="legend-items-horizontal">
              <div className="legend-item-compact">
                <div className="legend-indicator available"></div>
                <span>Libre</span>
              </div>
              <div className="legend-item-compact">
                <div className="legend-indicator occupied"></div>
                <span>Occupée</span>
              </div>
              <div className="legend-item-compact">
                <div className="legend-indicator reserved"></div>
                <span>Réservée</span>
              </div>
              <div className="legend-item-compact">
                <div className="legend-indicator maintenance"></div>
                <span>Maintenance</span>
              </div>
              <div className="legend-item-compact">
                <div className="legend-indicator cleaning"></div>
                <span>Nettoyage</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="floors-container">
        {floors.map(floor => {
          const stats = getFloorStats(floor);
          return (
            <div key={floor} className="floor-section-pro">
              <div className="floor-header-pro">
                <div className="floor-info">
                  <div className="floor-label-pro">
                    <span className="floor-icon">🏢</span>
                    <span className="floor-text">Étage {floor}</span>
                  </div>
                  <div className="floor-stats">
                    <span className="stat-item">{stats.total} chambres</span>
                    <span className="stat-separator">•</span>
                    <span className="stat-available">{stats.available} libres</span>
                    {stats.occupied > 0 && (
                      <>
                        <span className="stat-separator">•</span>
                        <span className="stat-occupied">{stats.occupied} occupées</span>
                      </>
                    )}
                    {stats.reserved > 0 && (
                      <>
                        <span className="stat-separator">•</span>
                        <span className="stat-reserved">{stats.reserved} réservées</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="floor-occupancy">
                  <div className="occupancy-bar">
                    <div
                      className="occupancy-fill"
                      style={{
                        width: `${((stats.occupied + stats.reserved) / stats.total) * 100}%`,
                        background: `linear-gradient(90deg, var(--occupied-coral) 0%, var(--reserved-blue) 100%)`
                      }}
                    ></div>
                  </div>
                  <span className="occupancy-text">
                    {Math.round(((stats.occupied + stats.reserved) / stats.total) * 100)}% occupé
                  </span>
                </div>
              </div>

              <div className="floor-rooms-pro">
                {roomsByFloor[floor]
                  .sort((a, b) => a.room_number.localeCompare(b.room_number))
                  .map(room => {
                    const statusInfo = getStatusDisplay(room);
                    const typeIcon = getRoomTypeIcon(room.type);
                    return (
                      <button
                        key={room.id}
                        className={`room-card-pro ${statusInfo.status}`}
                        onClick={() => onRoomClick(room.id)}
                        title={`${room.room_number} - ${statusInfo.label}${room.guest_name ? ` - ${room.guest_name}` : ''}`}
                        aria-label={`Chambre ${room.room_number}, ${statusInfo.label}${room.guest_name ? `, occupée par ${room.guest_name}` : ''}`}
                        type="button"
                      >
                        <div className="room-header">
                          <div className="room-number-pro">{room.room_number}</div>
                          <div className="room-status-indicator" style={{ backgroundColor: statusInfo.color }}></div>
                        </div>

                        <div className="room-content">
                          <div className="room-type-pro">
                            <span className="type-icon">{typeIcon}</span>
                            <span className="type-text">{room.type}</span>
                          </div>

                          {room.guest_name ? (
                            <div className="room-guest">
                              <span className="guest-icon">👤</span>
                              <span className="guest-name">
                                {room.guest_name.split(' ')[0]}
                              </span>
                            </div>
                          ) : (
                            <div className="room-available">
                              <span className="available-text">Disponible</span>
                            </div>
                          )}
                        </div>

                        <div className="room-footer">
                          <div className="room-price">€{room.price_per_night}/nuit</div>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RoomGrid;